/**
 * IdleTimeoutWarning Component
 * 
 * Displays a warning modal when user is about to be logged out due to inactivity.
 * Shows countdown timer and allows user to continue session.
 */

import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Clock, LogOut, RefreshCw } from 'lucide-react';

const IdleTimeoutWarning = ({ 
  isOpen, 
  remainingTime, 
  onContinue, 
  onLogout 
}) => {
  // Format remaining time
  const formatTime = (seconds) => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `0:${seconds.toString().padStart(2, '0')}`;
  };

  // Get urgency color based on time remaining
  const getUrgencyColor = () => {
    if (remainingTime <= 10) return 'text-red-600';
    if (remainingTime <= 30) return 'text-orange-500';
    return 'text-yellow-600';
  };

  // Get progress percentage
  const progressPercent = (remainingTime / 60) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Session Expiring Soon
          </DialogTitle>
          <DialogDescription>
            You will be automatically signed out due to inactivity.
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Countdown Circle */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-32 h-32">
              {/* Background circle */}
              <svg className="w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-gray-200"
                />
                {/* Progress circle */}
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 56}`}
                  strokeDashoffset={`${2 * Math.PI * 56 * (1 - progressPercent / 100)}`}
                  className={`${getUrgencyColor()} transition-all duration-1000`}
                  strokeLinecap="round"
                />
              </svg>
              {/* Time display */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Clock className={`h-5 w-5 mb-1 ${getUrgencyColor()}`} />
                <span className={`text-3xl font-bold ${getUrgencyColor()}`}>
                  {formatTime(remainingTime)}
                </span>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-600 text-center">
              Click "Continue Session" to stay signed in
            </p>
          </div>

          {/* Progress bar (alternative visual) */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full transition-all duration-1000 ${
                remainingTime <= 10 ? 'bg-red-500' :
                remainingTime <= 30 ? 'bg-orange-500' :
                'bg-yellow-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onLogout}
            className="flex-1"
            data-testid="idle-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out Now
          </Button>
          <Button 
            onClick={onContinue}
            className="flex-1 bg-teal-600 hover:bg-teal-700"
            data-testid="idle-continue-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Continue Session
          </Button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-2">
          For your security, inactive sessions are automatically ended after 15 minutes.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default IdleTimeoutWarning;
